/**
 * Step 3 — le dossier : titre, référence, dates clés, notes, tags.
 *
 * 9.BA.1 — les assureurs et les garanties ont quitté cet écran. Ils arrivaient
 * AVANT le cahier de charges : on demandait « à qui ? » avant que le courtier
 * puisse répondre « quoi ? ». Ils se choisissent désormais à la préparation de
 * la demande, où l'on sait ce qu'on envoie. Le nom du fichier reste
 * `Step3Coverage` pour ne pas déplacer un fichier dans un correctif de raccord.
 *
 * Bouton « Créer » disabled tant que le titre est vide.
 */

import { useTranslation } from "react-i18next";

import { TagsInput } from "../../../components/TagsInput";
import type { IntakeMvpData } from "../../../lib/intakeDraftStorage";

type Props = {
  data: IntakeMvpData;
  onChange: (next: Partial<IntakeMvpData>) => void;
};

export function Step3Coverage({ data, onChange }: Props) {
  const { t } = useTranslation();

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-slate-900">
          {t("projects.intake.step3.title")}
        </h2>
        <p className="mt-1 text-sm text-slate-600">{t("projects.intake.step3.subtitle")}</p>
      </div>

      <label className="block">
        <span className="label">{t("projects.fields.title")} *</span>
        <input
          className="input"
          value={data.title ?? ""}
          onChange={(e) => onChange({ title: e.target.value })}
          minLength={3}
          maxLength={200}
          required
        />
      </label>

      <label className="block">
        <span className="label">{t("projects.fields.reference")}</span>
        <input
          className="input"
          value={data.reference ?? ""}
          onChange={(e) => onChange({ reference: e.target.value })}
          maxLength={50}
        />
      </label>

      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block">
          <span className="label">{t("projects.fields.effect_date")}</span>
          <input
            type="date"
            className="input"
            value={data.effect_date ?? ""}
            onChange={(e) => onChange({ effect_date: e.target.value || undefined })}
          />
        </label>
        <label className="block">
          <span className="label">{t("projects.fields.renewal_target_date")}</span>
          <input
            type="date"
            className="input"
            value={data.renewal_target_date ?? ""}
            onChange={(e) => onChange({ renewal_target_date: e.target.value || undefined })}
          />
        </label>
        <label className="block">
          <span className="label">{t("projects.fields.deadline_date")}</span>
          <input
            type="date"
            className="input"
            value={data.deadline_date ?? ""}
            onChange={(e) => onChange({ deadline_date: e.target.value || undefined })}
          />
        </label>
      </div>

      <label className="block">
        <span className="label">{t("projects.fields.notes")}</span>
        <textarea
          className="input min-h-[6rem]"
          value={data.notes ?? ""}
          onChange={(e) => onChange({ notes: e.target.value })}
          maxLength={5000}
        />
      </label>

      <div>
        <span className="label">{t("projects.fields.tags")}</span>
        <TagsInput value={data.tags ?? []} onChange={(next) => onChange({ tags: next })} />
      </div>
    </div>
  );
}
